'use client'

import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import TransportBookingClient from '@/app/(booking)/booking/activities/[slug]/TransportBookingClient'
import type { Adventure } from '@/lib/data'
import type { Activity } from '@/lib/hooks/useActivities'
import type { PricePerUnit } from '@/types/availability'
import type { ActivityExtraField } from '@/types/availability'
import styles from './ServiceQuickAddModal.module.css'

type TransportActivityForModal = Activity & {
  baseOccupancy?: number
  extraGuestPrice?: PricePerUnit
  requiresReturnDate?: boolean
  extraFields?: ActivityExtraField[]
  contactInfo?: string
}

interface ServiceQuickAddModalProps {
  isOpen: boolean
  onClose: () => void
  activity: Adventure
  firestoreActivity: TransportActivityForModal
  prefillDates?: { checkIn?: string; checkOut?: string }
  prefillGuests?: number
  onAdded?: () => void
}

export default function ServiceQuickAddModal({
  isOpen,
  onClose,
  activity,
  firestoreActivity,
  prefillDates,
  prefillGuests,
  onAdded,
}: ServiceQuickAddModalProps) {
  const t = useTranslations('serviceQuickAdd')
  const [showToast, setShowToast] = useState(false)
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEsc)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = prevOverflow
    }
  }, [isOpen, onClose])

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose()
  }

  const handleAdded = () => {
    onAdded?.()
    setShowToast(true)
    setTimeout(() => {
      setShowToast(false)
      onClose()
    }, 2700)
  }

  if (!isOpen) return null

  const hasPrefillDates = prefillDates?.checkIn || prefillDates?.checkOut

  return (
    <div className={styles.overlay} ref={overlayRef} onClick={handleOverlayClick} role="dialog" aria-modal="true">
      <div className={styles.card}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t('modalTitle')}</h2>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {hasPrefillDates && (
          <p className={styles.datesHint}>{t('datesInherited')}</p>
        )}

        <div className={styles.body}>
          <TransportBookingClient
            activity={activity}
            firestoreActivity={firestoreActivity}
            embedded={true}
            prefillDates={prefillDates}
            prefillGuests={prefillGuests}
            onAdded={handleAdded}
          />
        </div>
      </div>

      {showToast && (
        <div className={styles.toast} role="status" aria-live="polite">
          {t('addedToast')}
        </div>
      )}
    </div>
  )
}
